// 每日經期紀錄的**列表**（W58.5）。
//
// **審計找到的洞（§2.3 的②）。** `GET /calendar/cycle/logs?start_date&end_date`
// 回傳一個日期範圍內的每日紀錄，前端**從來沒用過**（`getCycleLogs` 是一個
// 沒有人呼叫的 wrapper）。而日詳情只能**一天一天**地從月曆上點進去看 ——
// 所以「我這三個月記了哪些天、那天是什麼症狀」**沒有答案**。
//
// **這裡就是那個答案。** 最近 90 天有紀錄的日子，一列一列列出來。
//
// **為什麼是 90 天。** 三個月 = 三個週期，夠回答「我這幾輪記得怎麼樣」；
// 再多畫下去捲不動，而且一個頁面上的紀錄量超過三個週期就已經沒有比較對象了。
//
// **刪除是「歸屬由服務器判定」的那一條**（W58.4）：`DELETE /cycle/logs/{date}`
// 沒有 `owner_id` query，前端不送。多送會被 422。

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  CRAMP_LABEL,
  FLOW_LABEL,
  deleteCycleLog,
  getCycleLogs,
  type CrampValue,
  type FlowValue,
} from "@/lib/api/calendar";
import { queryKeys } from "@/lib/queries/keys";

export interface CycleDailyLogsProps {
  /** 今天（`YYYY-MM-DD`）。 */
  today: string;
}

const WINDOW_DAYS = 90;

export function CycleDailyLogs({ today }: CycleDailyLogsProps) {
  const qc = useQueryClient();

  const from = shiftIso(today, -WINDOW_DAYS);
  const to = today;

  const logsQuery = useQuery({
    queryKey: ["calendar-cycle-logs", from, to],
    queryFn: () => getCycleLogs({ startDate: from, endDate: to }),
  });

  const remove = useMutation({
    mutationFn: (date: string) => deleteCycleLog(date),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["calendar-cycle-logs"] });
      await qc.invalidateQueries({ queryKey: ["calendar-cycle"] });
      await qc.invalidateQueries({ queryKey: ["calendar-month"] });
      await qc.invalidateQueries({ queryKey: ["calendar-day"] });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  const logs = logsQuery.data ?? [];
  // **沒有紀錄時整節不渲染** —— 不是渲染一個空的「尚無紀錄」卡片。
  // 「尚無紀錄」是一個**答案**，而這裡沒有答案要給（沒有紀錄的時候，使用者
  // 要的答案是「下一節：週期階段」，不是「這裡什麼都沒有」）。
  if (logs.length === 0) return null;

  return (
    <section className="flex flex-col gap-s-2" data-testid="calendar-cycle-logs">
      <h2 className="text-micro uppercase tracking-wide text-ink-3">
        每日紀錄 · 最近 {WINDOW_DAYS} 天
      </h2>
      <ul
        className="flex flex-col divide-y divide-hairline rounded-md border border-hairline"
        data-testid="calendar-cycle-logs-list"
      >
        {logs.map((log) => (
          <li key={log.log_date} className="flex items-start gap-s-3 p-s-3">
            <span className="w-s-10 shrink-0 text-body-sm tabular-nums text-ink-1">
              {Number(log.log_date.slice(5, 7))}/{Number(log.log_date.slice(8, 10))}
            </span>
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-x-s-2 gap-y-1 text-body-sm text-ink-1">
                {log.flow ? (
                  <span className="rounded-pill bg-partner-tint px-s-2 py-s-0 text-meta text-partner-strong">
                    流量 {FLOW_LABEL[log.flow as FlowValue] ?? log.flow}
                  </span>
                ) : null}
                {log.cramps && log.cramps !== "none" ? (
                  <span className="rounded-pill bg-paper-deep px-s-2 py-s-0 text-meta text-ink-2">
                    痛經 {CRAMP_LABEL[log.cramps as CrampValue] ?? log.cramps}
                  </span>
                ) : null}
                {log.symptoms.length > 0 ? (
                  <span className="text-meta text-ink-2">{log.symptoms.join("、")}</span>
                ) : null}
                {!log.flow && !log.cramps && log.symptoms.length === 0 && !log.note ? (
                  <span className="text-meta text-ink-3">—</span>
                ) : null}
              </p>
              {log.note ? (
                <p className="mt-s-1 text-meta text-ink-2">{log.note}</p>
              ) : null}
              {/* Q19 的稽核透明性 —— 「誰按了儲存」與「這是誰的身體」是兩件事。 */}
              <p className="mt-s-1 text-meta text-ink-3">由 {log.recorded_by_name} 記錄</p>
            </div>
            <button
              type="button"
              className="min-h-11 min-w-11 shrink-0 px-s-1 -mx-s-1 text-meta font-semibold text-ink-2 hover:underline disabled:opacity-50"
              onClick={() => remove.mutate(log.log_date)}
              disabled={remove.isPending}
              aria-label={`刪除 ${log.log_date} 的每日紀錄`}
              data-testid={`calendar-cycle-log-delete-${log.log_date}`}
            >
              刪除
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** `YYYY-MM-DD` 加減天數（不做時區換算 —— 這是日曆日，不是時間戳）。 */
function shiftIso(day: string, days: number): string {
  const base = Date.parse(`${day}T00:00:00Z`);
  return new Date(base + days * 86_400_000).toISOString().slice(0, 10);
}
